import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, formatCurrency, formatPercent } from "../../../../utils/formatNumber";
import { Row, Col, Card } from "react-bootstrap";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart } from "@highcharts/react";
import { type DashboardStats, type PlanStrategyPlanList, type ScatterStats, type Limits } from "../../PricingPlanTable";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { IoArrowUpCircleSharp, IoArrowDownCircleSharp } from "react-icons/io5";
import Loading from "../../../../components/core/blocks/Loading";
import moment from "moment";
import { FaExpandArrowsAlt } from "react-icons/fa";
import { BiCollapse } from "react-icons/bi";
import { Flex } from "../../../../../styled-system/jsx";
import { inventoryBucketValues, type GroupedStats } from "../../pricingTypes";

interface Props {
  dashboardData:
    | {
        total: DashboardStats;
        date: DashboardStats[];
        plan_id: DashboardStats[];
        plan_strategies: PlanStrategyPlanList;
      }
    | undefined;
  isLoading: boolean;
  marketPlace: string;
  pricingPlan: {
    enroll_date: string;
  };
  fetching: boolean;
  singlePlan: boolean;
  scatterData: ScatterStats;
  setXYLimit?: (limits: Limits | null) => void;
  selectedPlanStrategy: string;
  inventoryMetrics: GroupedStats;
  setHealthSelection: (health: string | undefined) => void;
}
interface ChartProps {
  dashboardData: {
    date: DashboardStats[];
    plan_id: DashboardStats[];
  };
  isLoading: boolean;
  marketPlace?: string;
  enrollDate?: string;
  isExpanded?: boolean;
  scatterData?: ScatterStats;
  setXYLimit?: (limits: Limits | null) => void;
  singlePlan?: boolean;
}

const SimpleChart = ({
  dashboardData,
  isLoading,
  marketPlace,
  enrollDate,
  isExpanded,
  scatterData,
  setXYLimit,
  singlePlan,
}: ChartProps) => {
  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  if (scatterData !== undefined) {
    return (
      <ScatterChart
        isLoading={isLoading}
        marketPlace={marketPlace}
        isExpanded={isExpanded}
        scatterData={scatterData}
        setXYLimit={setXYLimit}
        singlePlan={singlePlan}
      />
    );
  }
  return <IncrementalGCDChart dashboardData={dashboardData} isLoading={isLoading} enrollDate={enrollDate} />;
};

const ScatterChart = ({
  isLoading,
  marketPlace,
  isExpanded,
  scatterData,
  setXYLimit,
  singlePlan,
}: Omit<ChartProps, "dashboardData">) => {
  const { t } = useTranslation(["pricingv2"]);
  const [regionSet, setRegionSet] = useState(-1);

  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  const scatterPoints = scatterData?.scatterData ?? [];
  const centerX = 0;
  const centerY = 0;
  const minX = scatterData?.minX || 0;
  const maxX = scatterData?.maxX || 0;
  const minY = scatterData?.minY || 0;
  const maxY = scatterData?.maxY || 0;

  let chartHeight = 435;
  if (isExpanded) {
    chartHeight = 600;
  } else if (singlePlan) {
    chartHeight = 335;
  }

  let markerRadius = 4;
  if (scatterPoints.length > 1000) {
    markerRadius = 2;
  }

  const series: Highcharts.SeriesOptionsType[] = [
    {
      type: "polygon",
      name: t(
        ($) =>
          $.pricingv2[
            "Products in this quadrant have experienced a rise in units sold and a gain in incremental GCD revenue."
          ],
      ),
      color: "rgba(0, 128, 0, 0.25)",
      data: [
        [centerX, centerY],
        [maxX, centerY],
        [maxX, maxY],
        [centerX, maxY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: t(
        ($) =>
          $.pricingv2["Products in this quadrant have experienced a decline in units sold and incurred some GCD cost."],
      ),
      color: "rgba(178, 34, 34, 0.25)",
      data: [
        [centerX, centerY],
        [minX, centerY],
        [minX, minY],
        [centerX, minY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: t(
        ($) =>
          $.pricingv2[
            "Products in this quadrant have experienced a rise in units sold and a gain in incremental GCD revenue."
          ],
      ),
      color: "rgba(255, 165, 0, 0.25)",
      data: [
        [centerX, centerY],
        [minX, centerY],
        [minX, maxY],
        [centerX, maxY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: t(
        ($) =>
          $.pricingv2[
            "Products in this quadrant have experienced a decline in units sold and a gain in incremental GCD revenue."
          ],
      ),
      color: "rgba(0, 120, 215, 0.25)",
      data: [
        [centerX, centerY],
        [maxX, centerY],
        [maxX, minY],
        [centerX, minY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      name: t(($) => $.pricingv2["Pricing Plans"]),
      type: "scatter",
      color: trellisPalette[3],
      data: scatterPoints,
      showInLegend: false,
      marker: {
        symbol: "circle",
        radius: markerRadius,
      },
      turboThreshold: 0,
    },
  ];

  const scatterOptions: Highcharts.Options = {
    chart: {
      type: "scatter",
      zooming: { type: "xy" },
      height: chartHeight,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    title: {
      text: undefined,
    },
    xAxis: {
      min: minX,
      max: maxX,
      title: { text: t(($) => $.pricingv2["Incremental GCD from Price Increases"]) },
    },
    yAxis: {
      min: minY,
      max: maxY,
      title: { text: t(($) => $.pricingv2["Incremental Units"]) },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this) {
        if (this.series.index <= 3) {
          return `${this.series.name}`;
        }

        return `<strong>${this.key}</strong><br/>${t(($) => $.pricingv2["Inc. GCD"])}: ${formatCurrency(this.x, {
          marketPlace: marketPlace,
        })}<br/>${t(($) => $.pricingv2["Inc. Units"])}: ${formatNumber(this.y, { decimalPlaces: 0 })}`;
      },
    },
    plotOptions: {
      series: {
        point: {
          events: {
            click: function (this: Highcharts.Point): void {
              if (this.series.index <= 3) {
                let maxX = 0;
                let maxY = 0;
                let minX = 0;
                let minY = 0;
                for (let i = 0; i < this.series.data.length; i++) {
                  const x = this.series.data[i]?.x ?? 0;
                  const y = this.series.data[i]?.y ?? 0;
                  if (x > maxX) {
                    maxX = x;
                  }
                  if (y > maxY) {
                    maxY = y;
                  }
                  if (x < minX) {
                    minX = x;
                  }
                  if (y < minY) {
                    minY = y;
                  }
                }

                if (regionSet === -1 && setXYLimit) {
                  setXYLimit({
                    incUnitsMin: minY,
                    incUnitsMax: maxY,
                    incContributionMin: minX,
                    incContributionMax: maxX,
                  });
                  setRegionSet(this.index);
                } else if (setXYLimit) {
                  setXYLimit(null);
                  setRegionSet(-1);
                }
                return;
              }
              // @ts-expect-error Getting type error here, so skipping
              const url = `/user/pricing/plan/${this.planId}`;
              window.open(url, "_blank");
            },
          },
        },
      },
    },
    series: series,
  };

  return <Chart options={scatterOptions} />;
};

const IncrementalGCDChart = ({ dashboardData, isLoading, enrollDate }: ChartProps) => {
  const { t } = useTranslation(["pricingv2"]);
  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  const categories = dashboardData.date
    .map((d) => {
      const date = moment(d.name);
      if (date.isAfter(enrollDate)) {
        return d.name;
      }
      return null;
    })
    .filter((d) => d !== null);

  const incValue = dashboardData.date.map((d) => d.inc_contribution);
  const minIncValue = incValue.reduce((a, b) => Math.min(a, b), 0);
  const asp = dashboardData.date.map((d) => d.asp);
  const referenceCR = dashboardData.date.map((d) => d.reference_cr);
  const maxCR = referenceCR.reduce((a, b) => Math.max(a, b), 0);
  const maxASP = asp.reduce((a, b) => Math.max(a, b), 0);

  const options: Highcharts.Options = {
    chart: {
      height: 340,
      type: "column",
      zooming: { type: "x" },
      style: {
        fontSize: "14px",
      },
    },
    title: {
      text: "",
    },
    yAxis: [
      {
        title: {
          text: "",
        },
        min: minIncValue,
      },
      {
        title: {
          text: t(($) => $.pricingv2["Reference CR"]),
        },
        opposite: true,
        min: 0,
        max: maxCR,
        tickInterval: 0.025,
        labels: {
          formatter: function () {
            return formatPercent(this.value);
          },
        },
      },
      {
        title: {
          text: undefined,
        },
        opposite: true,
        min: 0,
        max: maxASP,
        tickInterval: 0.025,
        labels: {
          enabled: false,
        },
      },
    ],
    xAxis: {
      title: {
        text: null,
      },
      labels: {
        enabled: false,
      },
      categories: categories,
    },
    legend: {
      enabled: true,
      verticalAlign: "bottom",
    },
    series: [
      {
        name: t(($) => $.pricingv2["Incremental GCD"]),
        data: incValue,
        color: trellisPalette[4],
        opacity: 1,
        lineWidth: 4,
        type: "spline",
        yAxis: 0,
        tooltip: {
          valuePrefix: "$",
        },
      },
      {
        name: t(($) => $.pricingv2.metricDebug["Contribution"]),
        data: incValue,
        color: trellisPalette[4],
        opacity: 0.2,
        lineWidth: 3,
        type: "area",
        yAxis: 0,
        tooltip: {
          valuePrefix: "$",
          valueDecimals: 2,
        },
        showInLegend: false,
      },
      {
        name: t(($) => $.pricingv2["Reference CR"]),
        data: referenceCR,
        color: trellisPalette[5],
        type: "spline",
        yAxis: 1,
        tooltip: {
          valuePrefix: "",
        },
      },
      {
        name: t(($) => $.pricingv2.metricDebug["ASP"]),
        data: asp,
        color: trellisPalette[3],
        type: "spline",
        yAxis: 2,
        visible: false,
        tooltip: {
          valuePrefix: "$",
        },
      },
    ],

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
            maxHeight: 200,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };

  return <Chart options={options} />;
};

const InventoryAwareChart = ({
  inventoryMetrics,
  setHealthSelection,
}: {
  inventoryMetrics: GroupedStats;
  setHealthSelection: (health: string | undefined) => void;
}) => {
  const { t } = useTranslation(["pricingv2"]);
  const bucketName = "count";

  const translateInventoryHealth = (health: string) => {
    switch (health) {
      case "healthy":
        return t(($) => $.pricingv2["Healthy"]);
      case "excess":
        return t(($) => $.pricingv2["Excess"]);
      case "overstock":
        return t(($) => $.pricingv2["Overstock"]);
      case "deficient":
        return t(($) => $.pricingv2["Deficient"]);
      default:
        return health.charAt(0).toUpperCase() + health.slice(1);
    }
  };

  const dates = Object.keys(inventoryMetrics).sort();

  const series = inventoryBucketValues.map((healthType, idx) => {
    return {
      name: translateInventoryHealth(healthType),
      color: trellisPalette[idx],
      data: dates.map((date) => {
        const keys = Object.keys(inventoryMetrics[date]?.health ?? {});
        if (keys.includes(healthType)) {
          return inventoryMetrics[date]?.health[healthType][bucketName] || 0;
        }
        return 0;
      }),
      stack: "health",
    };
  });

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
      height: 435,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: dates,
      crosshair: true,
      accessibility: {
        description: t(($) => $.pricingv2["Report Dates"]),
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: undefined,
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
      },
      series: {
        groupPadding: 0,
        events: {
          click: function (event: Highcharts.SeriesClickEventObject) {
            const seriesName: string = event.point.series.name.toLowerCase();
            setHealthSelection(seriesName);
          },
        },
      },
    },
    series,
  };

  return <Chart options={options} />;
};

const SalesForecast = ({
  dashboardData,
  marketPlace,
}: {
  dashboardData: {
    total: DashboardStats;
  };
  marketPlace: string;
}) => {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <Col>
      {dashboardData.total.inc_cashflow > 0 ?
        <div className="fs-4">{t(($) => $.pricingv2["Sales Above Expected"])}</div>
      : <div className="fs-4">{t(($) => $.pricingv2["Sales Below Expected"])}</div>}
      <div className={`fw-bold`} style={{ color: "#000", fontSize: "3rem" }}>
        {formatCurrency(dashboardData.total.inc_cashflow, {
          marketPlace: marketPlace,
          compact: true,
          addPlus: true,
        })}
      </div>
    </Col>
  );
};

const ExtendedMetricCards = ({
  singlePlan,
  dashboardData,
  fetching,
}: {
  singlePlan: boolean;
  dashboardData: {
    total: DashboardStats;
  };
  fetching: boolean;
}) => {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <>
      <Col>
        {singlePlan && <div className="fs-4">{t(($) => $.pricingv2["Reference CR"])}</div>}
        {!singlePlan && <div className="fs-4">{t(($) => $.pricingv2["CR"])}</div>}
        <div className="fs-2 fw-bold" style={{ color: "#777" }}>
          {fetching ? "-" : formatPercent(dashboardData.total.reference_cr)}
        </div>
      </Col>
      <Col>
        <div className="fs-4">{t(($) => $.pricingv2["Page Views"])}</div>
        <div className="fs-2 fw-bold" style={{ color: "#777" }}>
          {fetching ?
            "-"
          : formatNumber(dashboardData.total.page_views, {
              decimalPlaces: 0,
            })
          }
        </div>
      </Col>
    </>
  );
};

export function MetricCards({
  dashboardData,
  isLoading,

  marketPlace,
  fetching,
  singlePlan,
  pricingPlan,
  scatterData,
  setXYLimit,
  selectedPlanStrategy,
  inventoryMetrics,
  setHealthSelection,
}: Props) {
  const { t } = useTranslation(["pricingv2"]);
  const [priceOption, setPriceOption] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Row>
      <Col className="mb-1 px-4" xs={isExpanded ? 12 : 7}>
        <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
          <Card.Body className="p-4">
            <Row className="text-center fs-standard">
              <Col>
                {dashboardData && dashboardData.total.inc_contribution > 0 ?
                  <div className="fs-4">{t(($) => $.pricingv2["Incremental GCD from Price Increases"])}</div>
                : <div className="fs-4">{t(($) => $.pricingv2["Volume Retention Cost"])}</div>}
                <div className="fw-bold" style={{ fontSize: "3rem" }}>
                  {dashboardData === undefined || fetching ?
                    "-"
                  : formatCurrency(dashboardData.total.inc_contribution, {
                      marketPlace: marketPlace,
                      addPlus: true,
                    })
                  }
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {!isExpanded && (
          <>
            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    {dashboardData === undefined || fetching ?
                      "-"
                    : <SalesForecast dashboardData={dashboardData} marketPlace={marketPlace} />}
                  </Col>
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2["Units Above Expected"])}</div>
                    <div className="fw-bold" style={{ color: "#000", fontSize: "3rem" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatNumber(dashboardData.total.inc_units, {
                          addPlus: true,
                        })
                      }
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2["Total Sales"])}</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.total_sales, {
                          marketPlace: marketPlace,
                          compact: true,
                        })
                      }
                    </div>
                  </Col>
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2["Units Sold"])}</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ? "-" : formatNumber(dashboardData.total.total_units)}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2["Expected Sales"])}</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.target_sales, {
                          marketPlace: marketPlace,
                          compact: true,
                        })
                      }
                    </div>
                  </Col>
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2["Expected Units"])}</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ? "-" : formatNumber(dashboardData.total.target_units)}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  {singlePlan && (
                    <Col>
                      <div className="fs-4">{t(($) => $.pricingv2["Price Changes"])}</div>
                      <div
                        className="fs-2 fw-bold"
                        style={{ color: "#777", cursor: "pointer" }}
                        onClick={() => setPriceOption(!priceOption)}
                      >
                        {!priceOption ?
                          <>
                            <span className="px-2">
                              <span style={{ color: "#bbb" }}>
                                <IoArrowUpCircleSharp />
                              </span>
                              <span>
                                {dashboardData === undefined || fetching ?
                                  "-"
                                : formatNumber(dashboardData.total.price_increased, { decimalPlaces: 0 })}
                              </span>
                            </span>
                            <span className="px-2">
                              <span style={{ color: "#bbb" }}>
                                <IoArrowDownCircleSharp />
                              </span>{" "}
                              <span>
                                {dashboardData === undefined || fetching ?
                                  "-"
                                : formatNumber(dashboardData.total.price_decreased, { decimalPlaces: 0 })}
                              </span>
                            </span>
                          </>
                        : <>
                            <span className="px-2">
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatCurrency(dashboardData.total.price_increased_avg, {
                                  marketPlace: marketPlace,
                                  addPlus: true,
                                })
                              }
                            </span>{" "}
                            /{" "}
                            <span className="px-2">
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatCurrency(dashboardData.total.price_decreased_avg, {
                                  marketPlace: marketPlace,
                                })
                              }
                            </span>
                          </>
                        }
                      </div>
                    </Col>
                  )}
                  <Col>
                    <div className="fs-4">{t(($) => $.pricingv2.metricDebug["ASP"])}</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.asp, {
                          marketPlace: marketPlace,
                        })
                      }
                    </div>
                  </Col>
                  {singlePlan && (
                    <Col>
                      <div className="fs-4">{t(($) => $.pricingv2["Reference Price"])}</div>
                      <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                        {dashboardData === undefined || fetching ?
                          "-"
                        : formatCurrency(dashboardData.total.reference_price, {
                            marketPlace: marketPlace,
                          })
                        }
                      </div>
                    </Col>
                  )}

                  {dashboardData && !singlePlan && (
                    <ExtendedMetricCards singlePlan={singlePlan} dashboardData={dashboardData} fetching={fetching} />
                  )}
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </Col>
      <Col className="mb-1 px-4" xs={isExpanded ? 12 : 5}>
        <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
          <Card.Body className="p-4">
            <Flex
              gap="small"
              justifyContent={"space-between"}
              alignContent={"center"}
              paddingBottom={"medium"}
              wrap="wrap"
            >
              {selectedPlanStrategy !== "override_supply" && (
                <div className="fs-4 fw-bold">{t(($) => $.pricingv2["Dynamic Pricing Contribution"])}</div>
              )}
              {selectedPlanStrategy === "override_supply" && (
                <div className="fs-4 fw-bold">{t(($) => $.pricingv2["Inventory Health"])}</div>
              )}
              <span style={{ cursor: "pointer" }} onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ?
                  <BiCollapse />
                : <FaExpandArrowsAlt />}
              </span>
            </Flex>
            {selectedPlanStrategy !== "override_supply" && (
              <div>
                {dashboardData === undefined || fetching ?
                  <Loading fullPage={false} height={"365px"} />
                : <SimpleChart
                    dashboardData={dashboardData}
                    isLoading={isLoading}
                    marketPlace={marketPlace}
                    enrollDate={pricingPlan.enroll_date}
                    isExpanded={isExpanded}
                    scatterData={scatterData}
                    setXYLimit={setXYLimit}
                    singlePlan={singlePlan}
                  />
                }
              </div>
            )}

            {selectedPlanStrategy === "override_supply" && (
              <div>
                <InventoryAwareChart inventoryMetrics={inventoryMetrics} setHealthSelection={setHealthSelection} />
              </div>
            )}
          </Card.Body>
        </Card>

        {singlePlan && (
          <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
            <Card.Body className="p-4">
              <Row className="text-center fs-standard">
                {dashboardData && (
                  <ExtendedMetricCards singlePlan={singlePlan} dashboardData={dashboardData} fetching={fetching} />
                )}
              </Row>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
}
